import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

export interface NowPlayingInfo {
  title: string;
  artist: string;
  cover?: string | null;
  album?: string | null;
  source?: string;
}

export interface HistorySong {
  title: string;
  artist: string;
  album?: string;
  cover?: string;
  duration?: string;
  programStartTS?: string;
}

interface AudioContextState {
  isPlaying: boolean;
  nowPlaying: NowPlayingInfo | null;
  history: HistorySong[];
  isLoading: boolean;
  error: string | null;
  playerOpen: boolean;
  streamUrl: string;
  playStream: () => void;
  pauseStream: () => void;
  openPlayer: () => void;
  closePlayer: () => void;
  togglePlayer: () => void;
  clearError: () => void;
}

const AudioContext = createContext<AudioContextState | null>(null);

const POLL_INTERVAL = 15000;
const SECURENET_PLAYER_URL = 'https://streamdb7web.securenetsystems.net/cirruspremier/KMEE';

export function AudioProvider({ children }: { children: ReactNode }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [nowPlaying, setNowPlaying] = useState<NowPlayingInfo | null>(null);
  const [history, setHistory] = useState<HistorySong[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerOpen, setPlayerOpen] = useState(false);

  // Poll now-playing metadata
  useEffect(() => {
    let cancelled = false;

    const fetchNowPlaying = async () => {
      try {
        const { publicApi } = await import('@/lib/api');
        const res = await publicApi.nowPlaying.get();
        if (cancelled) return;

        const data = res.data as {
          title?: string;
          artist?: string;
          cover?: string | null;
          album?: string | null;
          history?: HistorySong[];
          source?: string;
        };

        setNowPlaying({
          title: data.title || 'Sunny 106.5',
          artist: data.artist || 'Streaming Live',
          cover: data.cover,
          album: data.album,
          source: data.source,
        });
        setHistory((current) => (Array.isArray(data.history) ? data.history : current));
        setIsLoading(false);
      } catch (e) {
        console.error('Failed to fetch now playing:', e);
        if (!cancelled) {
          setNowPlaying({ title: 'Sunny 106.5', artist: 'Streaming Live' });
          setIsLoading(false);
        }
      }
    };

    fetchNowPlaying();
    const id = window.setInterval(fetchNowPlaying, POLL_INTERVAL);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const focusFooterPlayer = useCallback(() => {
    document.getElementById('sunny-secure-player-footer')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, []);

  const playStream = useCallback(() => {
    setError(null);
    setPlayerOpen(true);
    focusFooterPlayer();
  }, [focusFooterPlayer]);

  const pauseStream = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const openPlayer = useCallback(() => {
    setPlayerOpen(true);
  }, []);

  const closePlayer = useCallback(() => {
    setPlayerOpen(false);
  }, []);

  const togglePlayer = useCallback(() => {
    if (isPlaying) {
      pauseStream();
    } else {
      playStream();
    }
  }, [isPlaying, pauseStream, playStream]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-clear errors
  useEffect(() => {
    if (!error) return;
    const timer = window.setTimeout(clearError, 5000);
    return () => window.clearTimeout(timer);
  }, [error, clearError]);

  const value: AudioContextState = {
    isPlaying,
    nowPlaying,
    history,
    isLoading,
    error,
    playerOpen,
    streamUrl: SECURENET_PLAYER_URL,
    playStream,
    pauseStream,
    openPlayer,
    closePlayer,
    togglePlayer,
    clearError,
  };

  return (
    <AudioContext.Provider value={value}>
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within an AudioProvider');
  }
  return context;
}
